import Personalize from '@contentstack/personalize-edge-sdk';
import { getCookie, setCookie, deleteCookie } from '@/helpers/cookies';
import { getUserData } from '@/helpers/local-storage';
import { setAuthUserInStorage } from '@/utils/auth/common/auth-storage';

export const p13nCookieName = '_cs_p13n';

declare global {
  interface Window {
    jstag?: {
      send: (data: {
        user_id?: string;
        email?: string;
        role?: string;
        first_name?: string;
        last_name?: string;
        _e?: string; // custom event name
        [key: string]: any; // additional custom properties
      }) => void;
    };
  }
}

interface P13nCookieData {
  uid: string;
  role: string;
}

interface PersonalizeOptions {
  attributes?: {
    uid?: string | boolean;
    impression?: string;
    set?: Record<string, any>;
  };
  request?: Request;
}

/** Server-side only: reads the user role from the p13n cookie. */
export async function getUserRoleFromCookie(): Promise<string | null> {
  try {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const p13nCookie = cookieStore.get(p13nCookieName);
    
    if (!p13nCookie?.value) {
      return null;
    }

    try {
      let cookieValue = p13nCookie.value;
      try {
        cookieValue = decodeURIComponent(cookieValue);
      } catch {
        cookieValue = p13nCookie.value;
      }
      
      const cookieData = JSON.parse(cookieValue);
      return cookieData?.role || null;
    } catch {
      return null;
    }
  } catch (error) {
    return null;
  }
}

/** Server-side only: reads uid + role from the p13n cookie. */
export async function getUserDataFromCookie(): Promise<P13nCookieData | null> {
  try {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const p13nCookie = cookieStore.get(p13nCookieName);
    
    if (!p13nCookie?.value) {
      return null;
    }

    try {
      let cookieValue = p13nCookie.value;
      try {
        cookieValue = decodeURIComponent(cookieValue);
      } catch {
        cookieValue = p13nCookie.value;
      }
      
      const cookieData = JSON.parse(cookieValue);
      
      if (cookieData?.uid && cookieData?.role) {
        return {
          uid: cookieData.uid,
          role: cookieData.role,
        };
      }
      
      return null;
    } catch {
      return null;
    }
  } catch (error) {
    return null;
  }
}

// `_cs_p13n` carries a uid for variant targeting only — not a session record. Written/cleared
// by <PersonalizationIdentity/>, which observes the stored profile.

/** Strips anything but alphanumeric/underscore/hyphen; rejects if either ends up empty. */
function validateP13nCookieData(uid: string, role: string): P13nCookieData | null {
  if (typeof uid !== 'string' || typeof role !== 'string') {
    return null;
  }

  const sanitizedUid = uid.replace(/[^a-zA-Z0-9_-]/g, '').trim();
  const sanitizedRole = role.replace(/[^a-zA-Z0-9_-]/g, '').trim();

  if (!sanitizedUid || !sanitizedRole) {
    return null;
  }

  return { uid: sanitizedUid, role: sanitizedRole };
}

/** Sets the p13n cookie (client-side only). */
export function setCsp13nCookie(uid: string, role: string): void {
  if (typeof window === 'undefined') return;

  const validatedData = validateP13nCookieData(uid, role);
  if (!validatedData) {
    console.error('Invalid cookie data provided');
    return;
  }

  const expires = new Date();
  expires.setDate(expires.getDate() + 7);

  const cookieValue = JSON.stringify({
    uid: validatedData.uid,
    role: validatedData.role,
  });

  setCookie(p13nCookieName, cookieValue, {
    path: '/',
    expires,
    sameSite: 'strict',
    secure: window.location.protocol === 'https:',
  });
}

/** Removes the p13n cookie (client-side only). */
export function removeCsp13nCookie(): void {
  if (typeof window === 'undefined') return;

  deleteCookie(p13nCookieName, { path: '/' });
}

/** Reads the p13n cookie (client-side only). */
export function getCsp13nCookieClient(): P13nCookieData | null {
  if (typeof window === 'undefined') return null;

  const cookieValue = getCookie(p13nCookieName);

  if (!cookieValue) return null;

  try {
    // getCookie already decodes
    const data = JSON.parse(cookieValue);
    
    if (data?.uid && data?.role) {
      return { uid: data.uid, role: data.role };
    }
    
    return null;
  } catch {
    return null;
  }
}

/** Initializes the Personalize SDK; usable client-side to set user attributes. */
export async function getPersonalizeInstance(options: PersonalizeOptions = {}) {
  try {
    Personalize.setEdgeApiUrl('https://personalize-edge.contentstack.com');

    const { uid, impression, set } = options.attributes || {};

    const initOptions: any = {};

    if (uid && typeof uid === 'string') {
      initOptions.userId = uid;
    }

    const personalizeSdk = await Personalize.init(
      process.env.NEXT_PUBLIC_P13N_ID || '',
      initOptions
    );

    // Fall back to the cookie's uid if none was passed (client-side)
    if (typeof window !== 'undefined' && !uid) {
      const cookieData = getCsp13nCookieClient();
      if (cookieData?.uid) {
        await personalizeSdk.setUserId(cookieData.uid, { preserveUserAttributes: true });
      }
    }

    if (impression) {
      await personalizeSdk.triggerImpression(impression);
    }

    if (set) {
      await personalizeSdk.set(set);
    }

    return personalizeSdk;
  } catch (error) {
    console.error('Error initializing Personalize SDK:', error);
    throw error;
  }
}

/** Sends user info to Lytics CDP (client-side only). */
export function sendUserInfoToLytics(
  uid: string,
  role: string,
  firstName: string,
  lastName: string,
  email: string
): void {
  if (typeof window === 'undefined') return;

  const user_id = getCookie('cs-personalize-user-uid') || '';

  if (window.jstag && typeof window.jstag.send === 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.group('Lytics User Info');
      console.table({
        user_id,
        email,
        role,
        first_name: firstName,
        last_name: lastName,
      });
      console.groupEnd();
    }

    window.jstag.send({
      user_id,
      email,
      role,
      first_name: firstName,
      last_name: lastName,
    });
  } else if (process.env.NODE_ENV === 'development') {
    console.warn('Lytics jstag not loaded yet');
  }
}

/** Tracks a custom Lytics event (client-side only). */
export function trackLyticsEvent(eventName: string, eventData: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;

  const cookieData = getCsp13nCookieClient();
  
  if (window.jstag && typeof window.jstag.send === 'function' && cookieData) {
    window.jstag.send({
      _e: eventName,
      user_id: cookieData.uid,
      ...eventData,
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Lytics event tracked: ${eventName}`, eventData);
    }
  }
}

/** Updates the stored role and syncs it to the p13n cookie, Personalize, and Lytics. */
export async function updateUserRole(newRole: string): Promise<void> {
  if (typeof window === 'undefined') return;

  const cookieData = getCsp13nCookieClient();
  if (!cookieData) {
    console.error('No user cookie found');
    return;
  }

  setCsp13nCookie(cookieData.uid, newRole);

  await getPersonalizeInstance({
    attributes: {
      uid: cookieData.uid,
      set: { role: newRole },
    },
  });

  // setAuthUserInStorage() (not a raw setItem) dispatches userDataUpdated, so listeners
  // (e.g. CatalogPageContent) pick up the change immediately instead of on next reload.
  const userData = getUserData();
  if (userData) {
    const updated: Record<string, any> = { ...userData, role: newRole };
    setAuthUserInStorage(updated);

    sendUserInfoToLytics(
      updated.uid,
      newRole,
      updated.firstName,
      updated.lastName,
      updated.email
    );
  }
}

